<template>
    <v-container fluid class="pa-6 workspace-selector-container" style="direction: rtl;">
        <v-row justify="center">
            <v-col cols="12" md="10" lg="8">
                <!-- Enhanced header with gradient background and better typography -->
                <div class="text-center mb-10 header-section">
                    <div class="header-gradient pa-8 mb-6">
                        <h1 class="display-2 font-weight-bold mb-4 white--text">
                            בחירת סביבת עבודה
                        </h1>
                        <p class="headline font-weight-light white--text opacity-90">
                            בחר את סביבת העבודה שברצונך לגשת אליה
                        </p>
                    </div>
                </div>

                <!-- Loading State -->
                <v-row v-if="isLoading" justify="center">
                    <v-col cols="12" class="text-center">
                        <v-progress-circular :size="80" :width="8" color="#0d2c6d" indeterminate></v-progress-circular>
                        <p class="mt-6 title font-weight-medium">טוען סביבות עבודה...</p>
                    </v-col>
                </v-row>

                <!-- Work Environments Grid -->
                <v-row v-else class="workspace-grid">
                    <v-col v-for="workspace in workEnvironments" :key="workspace._id" cols="12" sm="6" md="4"
                        class="mb-6">
                        <!-- Enhanced card design with better visual hierarchy and animations -->
                        <v-card class="workspace-card elevation-4"
                            :class="{ 'workspace-card--selected': selectedWorkspace === workspace._id }"
                            @click="selectWorkspace(workspace)" :loading="loadingWorkspace === workspace._id">
                            <!-- Added gradient header for each card -->
                            <div class="card-header-gradient pa-4">
                                <div class="d-flex align-center">
                                    <v-avatar color="#ffcc00" size="56" class="elevation-2">
                                        <v-icon size="28" color="#0d2c6d">mdi-briefcase</v-icon>
                                    </v-avatar>
                                    <div class="flex-grow-1 mr-4">
                                        <!-- Switched positions: description first, then name -->
                                        <h3 class="title font-weight-bold white--text">
                                            {{ workspace.description || 'אין תיאור זמין' }}
                                        </h3>
                                        <p class="body-1 white--text mb-2 font-weight-medium">
                                            {{ workspace.dataName }}
                                        </p>
                                    </div>
                                </div>
                            </div>

                            <v-card-text class="pa-6">
                                <div class="mb-4">
                                    <v-chip :color="getRoleColor(getUserWorkspaceRole(workspace))" text-color="white"
                                        class="mb-3 elevation-1" label>
                                        <v-icon left small>mdi-shield-account</v-icon>
                                        {{ getRoleText(getUserWorkspaceRole(workspace)) }}
                                    </v-chip>
                                </div>

                                <div class="workspace-info">
                                    <!-- Only show user count if role is owner -->
                                    <div v-if="getUserWorkspaceRole(workspace) === 'owner'" class="d-flex align-center mb-3">
                                        <v-icon small color="#0d2c6d" class="ml-2">mdi-account-multiple</v-icon>
                                        <span class="body-2 font-weight-medium" style="color: #0d2c6d;">
                                            {{ workspace.members.length }} משתמשים
                                        </span>
                                    </div>

                                    <div class="d-flex align-center mb-3">
                                        <v-icon small color="#ffcc00" class="ml-2">mdi-shield-check</v-icon>
                                        <span class="body-2 font-weight-medium" style="color: #374151;">
                                            {{ getPermissionsText(getUserPermissions(workspace)) }}
                                        </span>
                                    </div>

                                    <div class="d-flex align-center">
                                        <v-icon small color="grey" class="ml-2">mdi-clock-outline</v-icon>
                                        <span class="caption grey--text">
                                            עדכון אחרון: {{ formatDate(workspace.updatedAt) }}
                                        </span>
                                    </div>
                                </div>
                            </v-card-text>

                            <!-- Enhanced button with better styling -->
                            <v-card-actions class="pa-6 pt-0" >
                                <v-btn  large color="#0d2c6d" dark :loading="loadingWorkspace === workspace._id"
                                    @click.stop="selectWorkspace(workspace)" class="workspace-enter-btn elevation-2"
                                    :class="{ 'workspace-enter-btn--selected': selectedWorkspace === workspace._id }" style="min-width: 150px;">
                                    <v-icon left>mdi-login-variant</v-icon>
                                    כניסה לסביבה
                                </v-btn>
                            </v-card-actions>
                        </v-card>
                    </v-col>
                </v-row>

                <!-- Empty State -->
                <v-row v-if="!isLoading && workEnvironments.length === 0" justify="center">
                    <v-col cols="12" md="6" class="text-center">
                        <div class="empty-state pa-8">
                            <v-icon size="120" color="#ffcc00" class="mb-6">mdi-briefcase-off-outline</v-icon>
                            <h3 class="display-1 mb-4 font-weight-bold" style="color: #0d2c6d;">
                                אין סביבות עבודה זמינות
                            </h3>
                            <p class="title grey--text font-weight-regular">
                                נראה שאין לך גישה לאף סביבת עבודה כרגע.
                                פנה למנהל המערכת לקבלת הרשאות.
                            </p>
                        </div>
                    </v-col>
                </v-row>
            </v-col>
        </v-row>

        <!-- Snackbar for notifications -->
        <v-snackbar v-model="snackbar.show" :color="snackbar.color" :timeout="4000" bottom right>
            {{ snackbar.message }}
            <template v-slot:action="{ attrs }">
                <v-btn text v-bind="attrs" @click="snackbar.show = false">
                    סגור
                </v-btn>
            </template>
        </v-snackbar>
    </v-container>
</template>

<script>
import ApiServices from '@/services/api.service'
import Auth from '@/services/auth.service'

export default {
    name: 'WorkEnvironmentSelector',
    data() {
        return {
            workEnvironments: [],
            selectedWorkspace: null,
            loadingWorkspace: null,
            isLoading: true,
            progressShow: false,
            snackbar: {
                show: false,
                message: '',
                color: 'success'
            }
        }
    },
    computed: {
        primaryColor() {
            return '#0d2c6d'
        }
    },
    async mounted() {
        await this.checkAuthAndLoadWorkspaces()
    },
    methods: {
        async checkAuthAndLoadWorkspaces() {
            const token = localStorage.getItem('token')
            if (!token) {
                this.showSnackBar('נדרש להתחבר מחדש', 'error')
                this.$router.push('/login')
                return
            }
            await this.getWorkspaces()
        },

        async getWorkspaces() {
            this.progressShow = true
            this.isLoading = true
            let token = localStorage.getItem("token")
            try {
                let api = process.env.VUE_APP_BASE_URL + "/user/get_user_workspaces"
                const res = await fetch(api, ApiServices.requestOptions("GET", "", token))
                const jsonObject = await res.json()

                if (res.status >= 400) {
                    this.isLoading = false
                    this.showSnackBar("שגיאה בקבלת סביבות העבודה: " + jsonObject, "error")
                } else if (res.status === 200) {
                    this.workEnvironments = jsonObject
                    this.isLoading = false
                }
            } catch (error) {
                this.isLoading = false
                this.showSnackBar("שגיאה בטעינת סביבות העבודה: " + error, "error")
            } finally {
                this.progressShow = false
            }
        },

        getUserWorkspaceRole(workspace) {
            const userId = Auth.getUserId()
            if (!workspace.members || workspace.members.length === 0) return 'viewer'
            const member = workspace.members.find(m => m.user_id.toString() === userId.toString())
            return member.role || 'viewer'
        },
        getUserPermissions(workspace) {
            if (!workspace.members || workspace.members.length === 0) return []
            const member = workspace.members[0]

            if (member.hasTableLimit && member.tablePermissions && member.tablePermissions.length > 0) {
                return member.tablePermissions[0].permissions || []
            }

            return member.defaultPermissions || []
        },

        getRoleColor(role) {
            const colors = {
                owner: '#e53e3e',
                editor: '#ff8c00',
                viewer: '#38a169'
            }
            return colors[role] || '#718096'
        },

        getRoleText(role) {
            const roleTexts = {
                owner: 'בעלים',
                editor: 'עורך',
                viewer: 'צופה'
            }
            return roleTexts[role] || 'לא ידוע'
        },

        getPermissionsText(permissions) {
            if (!permissions || permissions.length === 0) return 'אין הרשאות'

            const permissionTexts = {
                read: 'קריאה',
                write: 'כתיבה',
                delete: 'מחיקה',
                scheduler: 'תזמון',
                webhook: 'וובהוק',
                import: 'שדות',
            }

            const translatedPermissions = permissions
                .map(p => permissionTexts[p] || p)
                .slice(0, 2)

            if (permissions.length > 2) {
                translatedPermissions.push(`+${permissions.length - 2}`)
            }

            return translatedPermissions.join(', ')
        },

        formatDate(dateString) {
            if (!dateString) return 'לא ידוע'
            const date = new Date(dateString)
            return date.toLocaleDateString('he-IL')
        },

        async selectWorkspace(workspace) {
            if (this.loadingWorkspace) return

            this.selectedWorkspace = workspace._id
            this.loadingWorkspace = workspace._id
            let token = localStorage.getItem("token")

            try {
                let api = process.env.VUE_APP_BASE_URL + "/auth/switch_workspace/" + workspace._id
                const res = await fetch(api, ApiServices.requestOptions("POST", "", token))
                const jsonObject = await res.json()
                if (res.status >= 400) {
                    this.loadingWorkspace = null
                    this.showSnackBar("שגיאה בקבלת סביבות העבודה: " + jsonObject, "error")
                } else if (res.status === 200) {
                    localStorage.setItem('token', jsonObject.token);
                    this.showSnackBar(`נכנסת לסביבת העבודה: ${workspace.dataName}`, 'success')
                    localStorage.setItem('hasMultipleWorkspaces', true);
                    this.$router.push('/user')
                }
            } catch (error) {
                this.showSnackBar('שגיאה בכניסה לסביבת העבודה', 'error')
            } finally {
                this.loadingWorkspace = null
            }
        },

        showSnackBar(message, color = 'success') {
            this.snackbar = {
                show: true,
                message,
                color
            }
        },

        manageUsers(workspace) {
            // Placeholder method - no action performed yet
            console.log('Managing users for workspace:', workspace.dataName)
            this.showSnackBar(`ניהול משתמשים עבור ${workspace.dataName} - בקרוב!`, 'info')
        }
    }
}
</script>

<style scoped>
/* Enhanced styling with modern design elements */
.workspace-selector-container {
    background: linear-gradient(135deg, #f0f4ff 0%, #e6f3ff 100%);
    min-height: 100vh;
}

.header-section {
    margin-bottom: 3rem;
}

.header-gradient {
    background: linear-gradient(135deg, #0d2c6d 0%, #1e3a8a 100%);
    border-radius: 20px;
    box-shadow: 0 10px 30px rgba(13, 44, 109, 0.3);
}

.workspace-grid {
    margin-top: 2rem;
}

.workspace-card {
    transition: all 0.4s cubic-bezier(0.25, 0.8, 0.25, 1);
    cursor: pointer;
    border-radius: 20px;
    overflow: hidden;
    background: #ffffff;
    border: 2px solid transparent;
}

.workspace-card:hover {
    transform: translateY(-8px) scale(1.02);
    box-shadow: 0 20px 40px rgba(13, 44, 109, 0.2) !important;
    border-color: #ffcc00;
}

.workspace-card--selected {
    border-color: #0d2c6d;
    box-shadow: 0 15px 35px rgba(13, 44, 109, 0.25) !important;
}

.card-header-gradient {
    background: linear-gradient(135deg, #0d2c6d 0%, #1e3a8a 100%);
    position: relative;
}

.card-header-gradient::after {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: linear-gradient(45deg, transparent 0%, rgba(255, 204, 0, 0.1) 100%);
}

.workspace-info {
    background: #f8fafc;
    border-radius: 12px;
    padding: 1rem;
    border-left: 4px solid #ffcc00;
}

.workspace-enter-btn {
    border-radius: 12px !important;
    font-weight: 600 !important;
    text-transform: none !important;
    transition: all 0.3s ease !important;
}

.workspace-enter-btn:hover {
    background-color: #1e3a8a !important;
    transform: translateY(-2px);
    box-shadow: 0 8px 20px rgba(13, 44, 109, 0.3) !important;
}

.workspace-enter-btn--selected {
    background: linear-gradient(135deg, #ffcc00 0%, #ffd700 100%) !important;
    color: #0d2c6d !important;
}

/* Added styling for user management button */
.workspace-manage-btn {
    border-radius: 12px !important;
    font-weight: 600 !important;
    text-transform: none !important;
    transition: all 0.3s ease !important;
    background: linear-gradient(135deg, #ffcc00 0%, #ffd700 100%) !important;
    color: #0d2c6d !important;
}

.workspace-manage-btn:hover {
    background: linear-gradient(135deg, #ffd700 0%, #ffed4e 100%) !important;
    transform: translateY(-2px);
    box-shadow: 0 8px 20px rgba(255, 204, 0, 0.3) !important;
}

.empty-state {
    background: #ffffff;
    border-radius: 20px;
    box-shadow: 0 10px 30px rgba(0, 0, 0, 0.1);
}

/* Added responsive design improvements */
@media (max-width: 600px) {
    .workspace-card:hover {
        transform: translateY(-4px) scale(1.01);
    }

    .header-gradient {
        border-radius: 15px;
        padding: 2rem !important;
    }
}
</style>
